import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  getRecipe, scaleRecipe, archiveRecipe, activateRecipe,
  getRecipeVersions, RecipeDetail as RDetail,
  RecipeIngredient, RecipeVersion, RecipeScaleResult,
} from './api';
import './recipes.css';
import DataTable from '../../shared/components/DataTable';

const ADMIN_ROLES = ['Owner', 'Admin'];

type Tab = 'detail' | 'scale' | 'versions';

const RecipeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { sessionToken, user } = useAuth();

  const [data, setData] = useState<RDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const [tab, setTab] = useState<Tab>('detail');

  // Scale state
  const [multiplier, setMultiplier] = useState('2');
  const [scaleResult, setScaleResult] = useState<RecipeScaleResult | null>(null);
  const [scaleLoading, setScaleLoading] = useState(false);
  const [scaleError, setScaleError] = useState('');

  // Versions state
  const [versions, setVersions] = useState<RecipeVersion[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);

  const isAdmin = ADMIN_ROLES.includes(user?.role || '');
  const isManager = user?.role === 'Manager';
  const isStaff = user?.role === 'Employee';

  const load = useCallback(() => {
    if (!id) return;
    setLoading(true);
    getRecipe(sessionToken, parseInt(id, 10))
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [sessionToken, id]);

  useEffect(() => { load(); }, [load]);

  const loadVersions = () => {
    if (!id) return;
    setVersionsLoading(true);
    getRecipeVersions(sessionToken, parseInt(id, 10))
      .then(r => setVersions(r.versions))
      .catch(() => {})
      .finally(() => setVersionsLoading(false));
  };

  const handleTabChange = (t: Tab) => {
    setTab(t);
    if (t === 'versions' && versions.length === 0) loadVersions();
  };

  const handleScale = () => {
    if (!id) return;
    const m = parseFloat(multiplier);
    if (!m || m <= 0) { setScaleError('Enter a positive number'); return; }
    setScaleLoading(true);
    setScaleError('');
    scaleRecipe(sessionToken, parseInt(id, 10), m)
      .then(setScaleResult)
      .catch(e => setScaleError(e.message))
      .finally(() => setScaleLoading(false));
  };

  const handleArchive = async () => {
    if (!id || !window.confirm('Archive this recipe? It will no longer be visible to staff.')) return;
    try {
      await archiveRecipe(sessionToken, parseInt(id, 10));
      setActionMsg('Recipe archived.');
      load();
    } catch (e: any) { setActionMsg(`Error: ${e.message}`); }
  };

  const handleActivate = async () => {
    if (!id) return;
    try {
      await activateRecipe(sessionToken, parseInt(id, 10));
      setActionMsg('Recipe activated.');
      load();
    } catch (e: any) { setActionMsg(`Error: ${e.message}`); }
  };

  if (loading) return <div className="rcp-loading">Loading recipe...</div>;
  if (error) return <div className="rcp-error">{error}</div>;
  if (!data) return null;

  const { recipe, ingredients, steps } = data;
  const canEdit = isAdmin || (isManager && recipe.status === 'Draft');

  const renderQty = (ing: RecipeIngredient, scaled?: RecipeIngredient) => {
    const src = scaled || ing;
    const qty = scaled ? src.scaled_quantity : src.quantity;
    if (qty == null) {
      return <span className="rcp-td-unscalable">{ing.quantity_text || '—'}</span>;
    }
    return <span className="rcp-td-qty">{qty} {ing.unit || ''}</span>;
  };

  return (
    <div className="rcp-page">
      <Link to="/recipes" className="rcp-back-link">← Recipes</Link>

      <div className="rcp-header">
        <div>
          <h1>{recipe.name}</h1>
          <div className="rcp-card-meta" style={{ marginTop: '0.4rem' }}>
            <span className="rcp-tag rcp-tag-type">{recipe.recipe_type}</span>
            <span className={`rcp-tag ${recipe.status === 'Active' ? 'rcp-tag-active' : recipe.status === 'Draft' ? 'rcp-tag-draft' : 'rcp-tag-archived'}`}>
              {recipe.status}
            </span>
            <span className="rcp-text-muted">v{recipe.version}</span>
          </div>
        </div>
        <div className="rcp-header-actions">
          <Link to={`/recipes/${id}/sop`} className="rcp-btn" target="_blank" rel="noopener noreferrer">
            📋 SOP View
          </Link>
          {canEdit && (
            <Link to={`/recipes/${id}/edit`} className="rcp-btn rcp-btn-primary">✏️ Edit</Link>
          )}
          {isAdmin && recipe.status !== 'Archived' && (
            <button className="rcp-btn rcp-btn-warning" onClick={handleArchive}>Archive</button>
          )}
          {isAdmin && recipe.status === 'Archived' && (
            <button className="rcp-btn rcp-btn-success" onClick={handleActivate}>Reactivate</button>
          )}
        </div>
      </div>

      {actionMsg && <div className="rcp-success-msg">{actionMsg}</div>}

      {/* Tabs */}
      {!isStaff && (
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '0' }}>
          {(['detail', 'scale', 'versions'] as Tab[]).map(t => (
            <button
              key={t}
              onClick={() => handleTabChange(t)}
              style={{
                padding: '0.5rem 1rem',
                border: 'none',
                borderBottom: tab === t ? '2px solid #6366f1' : '2px solid transparent',
                background: 'none',
                color: tab === t ? '#6366f1' : '#64748b',
                fontWeight: tab === t ? 600 : 400,
                cursor: 'pointer',
                fontSize: '0.9rem',
                textTransform: 'capitalize',
              }}
            >
              {t === 'detail' ? 'Detail' : t === 'scale' ? '⚖️ Scale' : '🕒 Versions'}
            </button>
          ))}
        </div>
      )}

      {/* ── DETAIL TAB ── */}
      {tab === 'detail' && (
        <>
          {/* Metadata */}
          <div className="rcp-section">
            <h2>Overview</h2>
            <div className="rcp-meta-grid">
              {recipe.category && (
                <div className="rcp-meta-item"><label>Category</label><span>{recipe.category}</span></div>
              )}
              {recipe.yield_quantity && (
                <div className="rcp-meta-item"><label>Yield</label><span>{recipe.yield_quantity} {recipe.yield_unit || ''}</span></div>
              )}
              {recipe.prep_time_minutes != null && (
                <div className="rcp-meta-item"><label>Prep Time</label><span>{recipe.prep_time_minutes} min</span></div>
              )}
              {recipe.cook_time_minutes != null && (
                <div className="rcp-meta-item"><label>Cook Time</label><span>{recipe.cook_time_minutes} min</span></div>
              )}
              {recipe.shelf_life && (
                <div className="rcp-meta-item"><label>Shelf Life</label><span>{recipe.shelf_life}</span></div>
              )}
            </div>
            {recipe.storage_instructions && (
              <div style={{ marginTop: '0.75rem' }}>
                <label style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: '#94a3b8' }}>Storage</label>
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem' }}>{recipe.storage_instructions}</p>
              </div>
            )}
            {recipe.notes && (
              <div style={{ marginTop: '0.75rem' }}>
                <label style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: '#94a3b8' }}>Notes</label>
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem' }}>{recipe.notes}</p>
              </div>
            )}
          </div>

          {/* Ingredients */}
          <div className="rcp-section">
            <h2>Ingredients</h2>
            {ingredients.length === 0 ? (
              <div className="rcp-empty">No ingredients added yet.</div>
            ) : (
              <div className="rcp-table-wrap">
                <DataTable
                  rows={ingredients}
                  rowKey={(ing) => ing.id}
                  caption="Ingredients"
                  moreLabel="Prep & flags"
                  empty="No ingredients."
                  columns={[
                    {
                      key: 'ingredient', header: 'Ingredient', priority: 'primary',
                      render: (ing, i) => (
                        <>
                          <span className="rcp-text-muted">{i + 1}. </span>
                          {ing.ingredient_type === 'Sub Recipe' ? (
                            <span>
                              <Link to={`/recipes/${ing.sub_recipe_id}`} className="rcp-back-link" style={{ margin: 0 }}>
                                🔗 {ing.sub_recipe_name}
                              </Link>
                              <span className="rcp-tag rcp-tag-type">sub-recipe</span>
                            </span>
                          ) : (
                            ing.ingredient_name || <em className="rcp-text-muted">Unknown</em>
                          )}
                          {ing.is_optional && <span className="rcp-text-muted"> (optional)</span>}
                        </>
                      ),
                    },
                    { key: 'qty', header: 'Qty', priority: 'summary', render: (ing) => renderQty(ing) },
                    {
                      key: 'flags', header: 'Flags', priority: 'summary',
                      render: (ing) => (
                        <>
                          {ing.is_unscalable && <span className="rcp-tag rcp-tag-archived">unscalable</span>}
                          {ing.needs_review && <span className="rcp-tag rcp-tag-review">review</span>}
                          {!ing.is_unscalable && !ing.needs_review && '—'}
                        </>
                      ),
                    },
                    { key: 'prep', header: 'Prep Note', priority: 'detail', render: (ing) => ing.preparation_note || '—' },
                  ]}
                />
              </div>
            )}
          </div>

          {/* Steps */}
          <div className="rcp-section">
            <h2>Steps</h2>
            {steps.length === 0 ? (
              <div className="rcp-empty">No steps added yet.</div>
            ) : (
              <ol className="rcp-steps-list">
                {steps.map(step => (
                  <li key={step.id} className="rcp-step-item">
                    <div className={`rcp-step-num ${step.critical_control_point ? 'rcp-step-ccp' : ''}`}>
                      {step.step_number}
                    </div>
                    <div className="rcp-step-body">
                      <div className="rcp-step-instruction">{step.instruction}</div>
                      <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.3rem', flexWrap: 'wrap' }}>
                        {step.station && <span className="rcp-tag rcp-tag-station">{step.station}</span>}
                        {step.critical_control_point && <span className="rcp-tag rcp-tag-ccp">⚠️ CCP</span>}
                      </div>
                      {step.staff_note && <div className="rcp-step-note">📌 {step.staff_note}</div>}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </>
      )}

      {/* ── SCALE TAB ── */}
      {tab === 'scale' && (
        <div className="rcp-section">
          <h2>⚖️ Scale Recipe</h2>
          <div className="rcp-scale-bar">
            <label>Multiplier</label>
            <input
              type="number"
              step="0.1"
              min="0.1"
              value={multiplier}
              onChange={e => setMultiplier(e.target.value)}
              className="rcp-scale-input"
            />
            <button onClick={handleScale} disabled={scaleLoading} className="rcp-btn rcp-btn-primary">
              {scaleLoading ? 'Calculating...' : 'Calculate'}
            </button>
            {scaleResult && recipe.yield_quantity && (
              <span className="rcp-scale-yield">
                → Yield: {scaleResult.scaled_yield_quantity} {recipe.yield_unit || ''}
              </span>
            )}
          </div>
          {scaleError && <div className="rcp-error">{scaleError}</div>}
          {scaleResult && (
            <div className="rcp-table-wrap">
              <DataTable
                rows={scaleResult.scaled_ingredients}
                rowKey={(_ing, i) => i}
                caption="Scaled ingredients"
                empty="Nothing to scale."
                columns={[
                  { key: 'name', header: 'Ingredient', priority: 'primary', render: (ing) => ing.ingredient_name || ing.sub_recipe_name || '—' },
                  {
                    key: 'scaled', header: 'Scaled', priority: 'summary',
                    render: (ing) => (ing.scaled_quantity != null
                      ? <strong>{ing.scaled_quantity} {ing.unit || ''}</strong>
                      : <span className="rcp-td-unscalable">{ing.quantity_text || '—'}</span>),
                  },
                  {
                    key: 'orig', header: 'Original', priority: 'summary',
                    render: (ing) => (ing.quantity != null ? `${ing.quantity} ${ing.unit || ''}` : ing.quantity_text || '—'),
                  },
                  { key: 'warn', header: 'Warning', priority: 'detail', render: (ing) => <span className="rcp-td-warn">{ing.warning || '—'}</span> },
                ]}
              />
            </div>
          )}
        </div>
      )}

      {/* ── VERSIONS TAB ── */}
      {tab === 'versions' && (
        <div className="rcp-section">
          <h2>🕒 Version History</h2>
          {versionsLoading ? (
            <div className="rcp-loading">Loading...</div>
          ) : versions.length === 0 ? (
            <div className="rcp-empty">No version history yet. Edits to Active recipes create snapshots here.</div>
          ) : (
            <div className="rcp-versions-list">
              {versions.map(v => (
                <div key={v.id} className="rcp-version-row">
                  <span className="rcp-version-badge">v{v.version}</span>
                  <div className="rcp-version-meta">
                    <div>
                      Saved by <strong>{v.updated_by_name || v.user_name || 'Unknown'}</strong>
                      {' · '}
                      {new Date(v.created_at).toLocaleString()}
                    </div>
                    {v.changed_fields && (Array.isArray(v.changed_fields) ? v.changed_fields : []).length > 0 && (
                      <div className="rcp-version-fields">
                        Changed: {(v.changed_fields as string[]).join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RecipeDetail;
